<script setup lang="ts">
  import {
    Calendar,
    CalendarSize,
    Floating,
    FloatingPlacement,
    type DateISO,
    type CalendarDateRange,
  } from '@travelata-libs/ui-kit';
  import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useCloseOnEscape } from '@/entities/searchForm';
  import DateFromFlexChips from './DateFromFlexChips.vue';

  const VIEWPORT_BOTTOM_GAP = 16;
  const MAX_SETTLE_FRAMES = 30;

  interface DateFromDropdownProps {
    anchorIso?: string;
    flexibleForCalendar?: number | null;
    flexible: number;
    minDate: string;
    maxDate: string;
    disabledFromDate?: string | null;
    visible: boolean;
    range?: boolean;
    rangeValue?: CalendarDateRange<DateISO> | null;
    withExactOption?: boolean;
    placement?: FloatingPlacement;
  }

  const props = withDefaults(defineProps<DateFromDropdownProps>(), {
    anchorIso: '',
    flexibleForCalendar: null,
    range: false,
    rangeValue: null,
    withExactOption: false,
    disabledFromDate: null,
    placement: FloatingPlacement.Bottom,
  });

  interface DateFromDropdownEmits {
    (e: 'date-click', iso: string): void;
    (e: 'day-click', iso: string): void;
    (e: 'day-hover', iso: string | null): void;
    (e: 'select-flexible', n: number): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DateFromDropdownEmits>();

  const dropdownRef = ref<HTMLElement | null>(null);
  let settleRaf = 0;

  const cancelSettle = (): void => {
    if (settleRaf) {
      window.cancelAnimationFrame(settleRaf);
      settleRaf = 0;
    }
  };

  const scrollBottomIntoView = (): void => {
    const el = dropdownRef.value;
    if (!el) {
      return;
    }
    const overflow = Math.ceil(el.getBoundingClientRect().bottom + VIEWPORT_BOTTOM_GAP - window.innerHeight);
    if (overflow > 0) {
      window.scrollBy({ top: overflow, behavior: 'smooth' });
    }
  };

  const scheduleScrollBottomIntoView = (): void => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      return;
    }
    cancelSettle();
    let previousBottom = Number.NaN;
    let frames = 0;
    const waitForSettle = (): void => {
      settleRaf = 0;
      const el = dropdownRef.value;
      if (!props.visible || !el) {
        return;
      }
      const bottom = el.getBoundingClientRect().bottom;
      if (bottom === previousBottom || frames >= MAX_SETTLE_FRAMES) {
        scrollBottomIntoView();
        return;
      }
      previousBottom = bottom;
      frames += 1;
      settleRaf = window.requestAnimationFrame(waitForSettle);
    };
    settleRaf = window.requestAnimationFrame(waitForSettle);
  };

  const onDateClick = (payload: DateISO | CalendarDateRange<DateISO> | null): void => {
    if (props.range) {
      return;
    }
    if (typeof payload === 'string') {
      emit('date-click', payload);
    } else if (payload?.start) {
      emit('date-click', payload.start);
    }
  };

  const onDayClick = (iso: string): void => {
    if (props.range) {
      emit('day-click', iso);
    }
  };

  const onDayHover = (iso: string | null): void => {
    if (props.range) {
      emit('day-hover', iso);
    }
  };

  const onSelectFlexible = (n: number) => {
    emit('select-flexible', n);
  };

  const onHide = (): void => {
    if (props.visible) {
      emit('close');
    }
  };

  useCloseOnEscape(
    () => props.visible,
    () => emit('close'),
  );

  onMounted(() => {
    watch(
      () => props.visible,
      (visible: boolean) => {
        if (visible) {
          nextTick(scheduleScrollBottomIntoView);
        } else {
          cancelSettle();
        }
      },
      { immediate: true },
    );
  });

  onBeforeUnmount(cancelSettle);
</script>

<template>
  <Floating
    :visible="visible"
    :placement="placement"
    :disable-flip="true"
    :teleported="false"
    @hide="onHide"
  >
    <template #reference>
      <slot name="reference" />
    </template>

    <template #floating>
      <div
        ref="dropdownRef"
        class="date-from-dropdown"
        data-testid="date-from-dropdown"
      >
        <Calendar
          data-testid="date-from-calendar"
          :model-value="range ? rangeValue : anchorIso"
          double
          :size="CalendarSize.M"
          :range="range"
          :flexible-days="range ? null : flexibleForCalendar"
          :with-footer="false"
          :min-date="minDate"
          :max-date="maxDate"
          :disabled-from-date="disabledFromDate"
          @date-click="onDateClick"
          @day-click="onDayClick"
          @day-hover="onDayHover"
        />

        <DateFromFlexChips
          :value="flexible"
          :with-exact-option="withExactOption"
          @select="onSelectFlexible"
        />
      </div>
    </template>
  </Floating>
</template>

<style scoped lang="scss">
  .date-from-dropdown {
    background: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-m);
    display: flex;
    flex-direction: column;
    padding: var(--spacing-l);
    width: max-content;
  }
</style>
